import { Camera, ChevronLeft, CircleCheckBig, Clock3, Gauge, MapPin, NotebookPen, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";

import { MeasurementForm } from "../components/MeasurementForm";
import { MeasurementValues } from "../components/MeasurementValues";
import { PoolReadingForm } from "../components/PoolReadingForm";
import { StatusBadge } from "../components/StatusBadge";
import { TimerCard } from "../components/TimerCard";
import { captureLocation } from "../lib/location";
import { createInterventionPhotoTokenRegistry, interventionPhotoUploadUrl } from "../lib/intervention-photo-upload";
import { isOfflineQueued } from "../lib/offline-link";
import { uploadThroughOutbox } from "../lib/offline-upload";
import { useActiveModules } from "../lib/active-modules";
import { groupActivitiesByCategory } from "../lib/operational-collection-records";
import { formatMinutes } from "../../shared/duration";
import type { UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";

type ActivityDraft = { key: string; id: string | null; jobTypeId: string | null; label: string };

/** Il campo datetime-local vuole l'ora locale senza fuso: la data grezza lo lascerebbe vuoto. */
function datetimeLocalValue(value: Date | string) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function InterventionPage({ session }: { session: UserSession }) {
  const { id = "" } = useParams();
  const canManage = session.membership.role !== "collaborator";
  const utils = trpc.useUtils();
  const intervention = trpc.interventions.get.useQuery({ id });
  const activeTimer = trpc.interventions.activeTimer.useQuery();
  const entries = trpc.interventions.timeEntries.useQuery({ interventionId: id });
  const photos = trpc.interventions.photos.useQuery({ interventionId: id });
  const measurements = trpc.measurements.forIntervention.useQuery({ interventionId: id });
  const sites = trpc.sites.list.useQuery(undefined, { enabled: canManage });
  const team = trpc.team.list.useQuery(undefined, { enabled: canManage });
  const catalog = trpc.catalog.list.useQuery(undefined, { enabled: canManage });
  // Il registro piscine e un modulo: se l'azienda non ce l'ha, la sua API non si chiama nemmeno.
  const { attivi } = useActiveModules();
  const registroPiscine = attivi.has("pool_log");
  const pools = trpc.pools.list.useQuery(undefined, { enabled: registroPiscine });
  const start = trpc.interventions.startTimer.useMutation();
  const stop = trpc.interventions.stopTimer.useMutation();
  const manual = trpc.interventions.addManualTime.useMutation();
  const complete = trpc.interventions.complete.useMutation();
  const toggleActivity = trpc.interventions.toggleActivity.useMutation();
  const saveNotes = trpc.interventions.saveFieldNotes.useMutation();
  const update = trpc.interventions.update.useMutation();
  const [uploading, setUploading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [activityDrafts, setActivityDrafts] = useState<ActivityDraft[]>([]);
  /** L'attività di misurazione aperta adesso: si apre col tocco e si chiude appena salva. */
  const [measuringActivityId, setMeasuringActivityId] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  /** Esito della chiusura, scritto accanto al pulsante: in cima alla pagina nessuno lo vedrebbe. */
  const [closing, setClosing] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [notesState, setNotesState] = useState<{ tone: "ok" | "error" | "queued"; text: string } | null>(null);
  const photoTokens = useRef(createInterventionPhotoTokenRegistry());
  const draftKeys = useRef(0);

  async function refresh() {
    await Promise.all([
      utils.interventions.get.invalidate({ id }), utils.interventions.activeTimer.invalidate(),
      utils.interventions.timeEntries.invalidate({ interventionId: id }), utils.interventions.photos.invalidate({ interventionId: id }),
      utils.interventions.list.invalidate(), utils.measurements.forIntervention.invalidate({ interventionId: id }),
    ]);
  }
  /**
   * L'errore non viene fermato qui: la scheda del timer lo mostra e torna indietro
   * da sola. Fermandolo qui resterebbe a schermo un lavoro avviato che non esiste.
   */
  async function startWork() {
    const location = await captureLocation();
    await start.mutateAsync({ interventionId: id, ...location });
    setMessage("Lavoro iniziato e registrato.");
    // Il lavoro è già scritto: se il ricarico delle liste non riesce non si torna
    // indietro, i dati si rileggono al giro successivo.
    try { await refresh(); } catch { /* le query si ricaricano da sole */ }
  }
  async function stopWork() {
    // Uscire in silenzio lascerebbe la scheda ferma su "Pronto a iniziare" con il
    // timer ancora acceso sul server: meglio dirlo e far tornare indietro la scheda.
    if (!activeTimer.data) throw new Error("Il timer non risulta ancora avviato, riprova fra un istante.");
    const location = await captureLocation();
    await stop.mutateAsync({ timeEntryId: activeTimer.data.id, ...location });
    setMessage("Ore registrate correttamente.");
    try { await refresh(); } catch { /* le query si ricaricano da sole */ }
  }
  async function uploadPhoto(event: ChangeEvent<HTMLInputElement>, category: "before" | "during" | "after") {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true); setMessage(null);
    const token = photoTokens.current.get(category, file);
    try {
      // Il caricamento passa dal punto unico: senza rete la fotografia resta sul
      // dispositivo con il suo intervento e parte da sola, come ogni altra scrittura.
      const esito = await uploadThroughOutbox({
        url: interventionPhotoUploadUrl(id, category, token.photoId),
        field: "file",
        filename: file.name,
        blob: file,
        label: "Intervento: fotografia",
      });
      photoTokens.current.complete(token.fingerprint);
      if (!esito.inviato) { setMessage("Fotografia salvata sul dispositivo, parte appena torna la rete."); return; }
      await refresh(); setMessage("Fotografia caricata.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Caricamento non riuscito."); }
    finally { setUploading(false); event.target.value = ""; }
  }
  async function addManual(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await manual.mutateAsync({ interventionId: id, startedAt: new Date(String(data.get("startedAt"))), endedAt: new Date(String(data.get("endedAt"))), reason: String(data.get("reason")) });
      await refresh(); setManualOpen(false); setMessage("Ore manuali aggiunte.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Registrazione non riuscita."); }
  }
  async function finish() {
    setClosing(null);
    try {
      // Chi chiude con il proprio timer acceso lo ferma qui, con la posizione come sempre;
      // gli altri timer ancora accesi su questo lavoro li ferma il server alla chiusura.
      const mioTimer = activeTimer.data?.interventionId === id ? activeTimer.data : null;
      if (mioTimer) {
        const location = await captureLocation();
        await stop.mutateAsync({ timeEntryId: mioTimer.id, ...location });
      }
      const esito = await complete.mutateAsync({ id });
      if (isOfflineQueued(esito)) {
        setClosing({ tone: "ok", text: "Chiusura salvata sul telefono: parte da sola appena torna la rete." });
        return;
      }
      try { await refresh(); } catch { /* le query si ricaricano da sole */ }
      setClosing({ tone: "ok", text: "Lavoro concluso e registrato. Il timer è fermo." });
    } catch (error) {
      setClosing({ tone: "error", text: error instanceof Error ? error.message : "Chiusura non riuscita, riprova." });
    }
  }
  async function storeNotes(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Si rilegge dal modulo: la dettatura riempie il campo senza passare dallo stato.
    const notes = String(new FormData(event.currentTarget).get("fieldNotes") ?? "");
    setNotesState(null);
    try {
      const esito = await saveNotes.mutateAsync({ interventionId: id, notes });
      if (isOfflineQueued(esito)) { setNotesState({ tone: "queued", text: "Salvate sul telefono, partono appena torna la rete." }); return; }
      await utils.interventions.get.invalidate({ id });
      setNotesState({ tone: "ok", text: "Note salvate." });
    } catch (error) {
      setNotesState({ tone: "error", text: error instanceof Error ? error.message : "Salvataggio non riuscito, riprova." });
    }
  }
  async function markActivity(activityId: string, completed: boolean) {
    try {
      await toggleActivity.mutateAsync({ interventionId: id, activityId, completed });
      await utils.interventions.get.invalidate({ id });
    } catch (error) { setMessage(error instanceof Error ? error.message : "Aggiornamento non riuscito."); }
  }

  function openEditor() {
    if (!intervention.data) return;
    setEditError(null);
    setActivityDrafts(intervention.data.activities.map((activity) => ({
      key: `esistente-${activity.id}`,
      id: activity.id,
      jobTypeId: activity.jobTypeId,
      label: activity.label,
    })));
    setEditOpen(true);
  }
  function addActivityDraft(jobTypeId: string | null, label: string) {
    draftKeys.current += 1;
    setActivityDrafts((drafts) => [...drafts, { key: `nuova-${draftKeys.current}`, id: null, jobTypeId, label }]);
  }
  function removeActivityDraft(key: string) {
    setActivityDrafts((drafts) => drafts.filter((draft) => draft.key !== key));
  }
  async function saveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setEditError(null);
    // I valori si rileggono dal modulo vero e non dallo stato: dettatura e incolla
    // riempiono il campo senza emettere l'evento che aggiornerebbe la memoria del programma.
    const data = new FormData(event.currentTarget);
    const labels = data.getAll("activityLabel").map(String);
    const activityIds = data.getAll("activityId").map(String);
    const jobTypeIds = data.getAll("activityJobTypeId").map(String);
    const activities = labels
      .map((label, index) => ({
        id: activityIds[index] || null,
        jobTypeId: jobTypeIds[index] || null,
        label: label.trim(),
      }))
      .filter((activity) => activity.label.length > 0);
    const assigneeIds = data.getAll("assigneeIds").map(String);
    const scheduledStart = String(data.get("scheduledStart") || "");

    if (activities.length === 0) { setEditError("Lascia almeno un'attività sull'intervento."); return; }
    if (activities.some((activity) => activity.label.length < 2)) { setEditError("Ogni attività ha bisogno di un nome."); return; }
    if (assigneeIds.length === 0) { setEditError("Assegna almeno un collaboratore."); return; }
    if (!scheduledStart) { setEditError("Indica data e ora dell'intervento."); return; }

    try {
      await update.mutateAsync({
        id,
        siteId: String(data.get("siteId")),
        title: String(data.get("title") || "").trim(),
        description: String(data.get("description") || "").trim() || null,
        scheduledStart: new Date(scheduledStart),
        scheduledEnd: null,
        priority: Number(data.get("priority")),
        assigneeIds,
        activities,
        confirmSiteAssignments: true,
      });
      await refresh();
      setEditOpen(false);
      setMessage("Intervento aggiornato.");
    } catch (error) {
      setEditError(error instanceof Error ? error.message : "Modifica non riuscita.");
    }
  }

  if (intervention.isLoading) return <main className="page"><p className="loading-copy">Caricamento intervento...</p></main>;
  if (!intervention.data) return <main className="page"><p className="form-error">Intervento non trovato.</p></main>;
  const item = intervention.data;
  const ownActiveTimer = activeTimer.data?.interventionId === id ? activeTimer.data : null;
  const totalMinutes = (entries.data ?? []).filter((entry) => entry.endedAt && !entry.supersededAt).reduce((sum, entry) => sum + (new Date(entry.endedAt!).getTime() - new Date(entry.startedAt).getTime()) / 60_000, 0);
  const activityGroups = groupActivitiesByCategory(item.activities);
  // Le misurazioni arrivano dalla piu recente: la prima di ogni attività e la sua ultima lettura.
  const ultimaMisurazione = (activityId: string) => (measurements.data ?? []).find((misura) => misura.activityId === activityId);
  const isOpen = item.status !== "completed" && item.status !== "cancelled";
  // Le piscine di questa struttura, e solo quelle: chi e sul posto scrive i valori qui,
  // senza andare a cercare un'altra schermata.
  const piscineDelPosto = (pools.data ?? []).filter((piscina) => piscina.siteId === item.siteId && piscina.active);
  const canEdit = canManage && isOpen;

  return (
    <main className="page intervention-detail">
      {/* Chi lavora sul campo arriva da Oggi e li torna; il titolare torna all'elenco completo. */}
      {canManage
        ? <Link className="back-link" to="/interventi"><ChevronLeft size={18} /> Torna agli interventi</Link>
        : <Link className="back-link" to="/"><ChevronLeft size={18} /> Torna ai lavori di oggi</Link>}
      <header className="detail-hero">
        <div className="detail-hero__main">
          <div><StatusBadge status={item.status} /><span className="tag">Priorità {item.priority}</span></div>
          {/* In primo piano la struttura, poi il titolo del lavoro: stessa gerarchia degli elenchi. */}
          <h1>{item.siteName}</h1>
          <p className="detail-hero__work">{item.title}</p>
          <p>{item.description || "Nessuna nota aggiuntiva."}</p>
          {canEdit && <button className="button button--outline" type="button" onClick={openEditor}><Pencil size={17} /> Modifica intervento</button>}
        </div>
        <dl>
          <div><dt><MapPin size={17} /> Struttura</dt><dd>{item.siteName}<small>{item.siteAddress}</small></dd></div>
          <div><dt><Clock3 size={17} /> Quando</dt><dd>{new Date(item.scheduledStart).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}<small>ore {new Date(item.scheduledStart).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</small></dd></div>
        </dl>
      </header>

      {editOpen && (
        <form className="editor-card" onSubmit={saveEdit}>
          <div className="editor-card__heading"><h2>Modifica intervento</h2><p>Data, orario, attività e assegnazioni. Ogni modifica resta registrata.</p></div>
          <div className="form-grid">
            <label><span>Titolo</span><input name="title" defaultValue={item.title} placeholder="Manutenzione settimanale" /></label>
            <label><span>Struttura</span><select name="siteId" defaultValue={item.siteId}>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
            <label><span>Data e ora</span><input name="scheduledStart" type="datetime-local" defaultValue={datetimeLocalValue(item.scheduledStart)} /></label>
            <label><span>Priorità</span><select name="priority" defaultValue={String(item.priority)}><option value="1">Alta</option><option value="2">Normale</option><option value="3">Bassa</option></select></label>
            <fieldset className="form-grid__wide assignee-field">
              <legend>Collaboratori</legend>
              <div>
                {team.data?.filter((member) => member.active).map((member) => (
                  <label className="check-control" key={member.userId}>
                    <input type="checkbox" name="assigneeIds" value={member.userId} defaultChecked={item.assignments.some((assignment) => assignment.userId === member.userId)} /> {member.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="form-grid__wide activity-editor">
              <legend>Attività</legend>
              <ul>
                {activityDrafts.map((draft) => (
                  <li key={draft.key}>
                    <input type="hidden" name="activityId" value={draft.id ?? ""} />
                    <input type="hidden" name="activityJobTypeId" value={draft.jobTypeId ?? ""} />
                    <input name="activityLabel" defaultValue={draft.label} aria-label={`Attività ${draft.label || "nuova"}`} />
                    <button type="button" className="icon-button" aria-label={`Togli l'attività ${draft.label}`} onClick={() => removeActivityDraft(draft.key)}><Trash2 size={17} /></button>
                  </li>
                ))}
              </ul>
              <div className="activity-editor__add">
                <label>
                  <span>Aggiungi dal catalogo</span>
                  <select
                    aria-label="Aggiungi attività dal catalogo"
                    value=""
                    onChange={(event) => {
                      const jobTypeId = event.target.value;
                      if (!jobTypeId) return;
                      const job = catalog.data?.flatMap((category) => category.jobTypes).find((entry) => entry.id === jobTypeId);
                      if (job) addActivityDraft(job.id, job.name);
                      event.target.value = "";
                    }}
                  >
                    <option value="">Scegli un lavoro</option>
                    {catalog.data?.map((category) => (
                      <optgroup key={category.id} label={category.name}>
                        {category.jobTypes.filter((job) => job.active).map((job) => <option key={job.id} value={job.id}>{job.name}</option>)}
                      </optgroup>
                    ))}
                  </select>
                </label>
                <button type="button" className="button button--ghost" onClick={() => addActivityDraft(null, "")}><Plus size={17} /> Attività libera</button>
              </div>
            </fieldset>
            <label className="form-grid__wide"><span>Note</span><textarea name="description" rows={3} defaultValue={item.description ?? ""} placeholder="Istruzioni, accessi, attenzioni particolari" /></label>
          </div>
          {editError && <p className="form-error" role="alert">{editError}</p>}
          <div className="form-actions">
            <button type="button" className="button button--ghost" onClick={() => setEditOpen(false)}>Annulla</button>
            <button className={`button button--primary${update.isPending ? " button--saving" : ""}`}>{update.isPending ? "Salvataggio..." : "Salva modifiche"}</button>
          </div>
        </form>
      )}

      {/* Un timer rimasto acceso su un lavoro gia chiuso (prima che la chiusura lo fermasse)
          deve potersi fermare: senza la scheda l'operaio non potrebbe piu avviarne un altro. */}
      {(isOpen || ownActiveTimer) && <TimerCard activeSince={ownActiveTimer ? new Date(ownActiveTimer.startedAt) : null} onStart={startWork} onStop={stopWork} busy={start.isPending || stop.isPending} />}
      {message && <p className="notice" role="status">{message}</p>}

      <div className="detail-columns">
        <section className="detail-card">
          <div className="section-heading"><div><p className="eyebrow">Cosa fare</p><h2>Attività</h2></div><span className="metric">{item.activities.filter((activity) => activity.completed).length}/{item.activities.length}</span></div>
          {/* Prima il lavoro generico, poi le attività specifiche che lo compongono. */}
          {activityGroups.map((group) => (
            <div className="activity-category" key={group.category}>
              <h3>{group.category}</h3>
              <ul className="activity-list">
                {group.items.map((activity) => {
                  // Una attività di misurazione non si spunta: si tocca, si scrivono i numeri
                  // e il salvataggio la chiude da solo. La spunta senza valori direbbe "fatta"
                  // senza dire che cosa e stato letto.
                  if (activity.kind === "measurement" && activity.measurementFields.length > 0) {
                    const ultima = ultimaMisurazione(activity.id);
                    const aperta = measuringActivityId === activity.id;
                    return (
                      <li className="activity-list__measure" key={activity.id}>
                        <button
                          type="button"
                          className="activity-measure"
                          aria-expanded={aperta}
                          aria-label={`Scrivi i valori di ${activity.label}`}
                          onClick={() => setMeasuringActivityId(aperta ? null : activity.id)}
                          disabled={!isOpen}
                        >
                          <Gauge size={23} className={activity.completed ? "is-done" : ""} aria-hidden="true" />
                          <span className="activity-measure__body">
                            <span className={activity.completed ? "is-done" : ""}>{activity.label}</span>
                            {ultima
                              ? <MeasurementValues values={ultima.values} />
                              : <small>{isOpen ? "Tocca per scrivere i valori" : "Nessuna misurazione"}</small>}
                          </span>
                        </button>
                        {aperta && isOpen && (
                          <MeasurementForm
                            interventionId={id}
                            activityId={activity.id}
                            activityLabel={activity.label}
                            fields={activity.measurementFields}
                            onSaved={async () => { setMeasuringActivityId(null); await refresh(); setMessage("Misurazione registrata."); }}
                            onCancel={() => setMeasuringActivityId(null)}
                          />
                        )}
                      </li>
                    );
                  }
                  return (
                    <li key={activity.id}>
                      <button type="button" className="activity-check" aria-label={`${activity.completed ? "Riapri" : "Segna come fatta"}: ${activity.label}`} onClick={() => void markActivity(activity.id, !activity.completed)} disabled={toggleActivity.isPending || !isOpen}>
                        <CircleCheckBig size={23} className={activity.completed ? "is-done" : ""} />
                      </button>
                      <span className={activity.completed ? "is-done" : ""}>{activity.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
        <section className="detail-card"><div className="section-heading"><div><p className="eyebrow">Tempo</p><h2>Ore registrate</h2></div><span className="metric">{formatMinutes(totalMinutes)}</span></div><ul className="time-list">{entries.data?.filter((entry) => !entry.supersededAt).map((entry) => <li key={entry.id}><span><strong>{new Date(entry.startedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} - {entry.endedAt ? new Date(entry.endedAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "in corso"}</strong><small>{entry.userName} · {entry.origin === "manual" ? `Manuale: ${entry.reason}` : entry.origin === "corrected" ? `Corretto: ${entry.reason}` : "Timer"}</small></span></li>)}</ul><button className="text-button" type="button" onClick={() => setManualOpen(!manualOpen)}>+ Aggiungi ore manuali</button></section>
      </div>

      {manualOpen && <form className="editor-card editor-card--compact" onSubmit={addManual}><div className="editor-card__heading"><h2>Inserisci ore manuali</h2><p>Indica sempre il motivo per mantenere lo storico trasparente.</p></div><div className="form-grid"><label><span>Inizio</span><input name="startedAt" type="datetime-local" required /></label><label><span>Fine</span><input name="endedAt" type="datetime-local" required /></label><label className="form-grid__wide"><span>Motivo</span><input name="reason" minLength={5} required placeholder="Timer dimenticato" /></label></div><div className="form-actions"><button type="button" className="button button--ghost" onClick={() => setManualOpen(false)}>Annulla</button><button className="button button--primary">Registra ore</button></div></form>}

      <section className="detail-card field-notes">
        <div className="section-heading"><div><p className="eyebrow">Dal campo</p><h2>Note e materiali</h2></div><NotebookPen size={20} aria-hidden="true" /></div>
        {isOpen ? (
          <form onSubmit={storeNotes}>
            <label>
              <span>Materiali usati, problemi trovati, cose da dire</span>
              <textarea
                key={item.fieldNotes ?? ""}
                name="fieldNotes"
                rows={4}
                maxLength={2000}
                defaultValue={item.fieldNotes ?? ""}
                placeholder="Esempio: 2 sacchi di concime, 1 irrigatore sostituito, cancello che non chiude"
              />
            </label>
            <button className={`button button--primary field-notes__save${saveNotes.isPending ? " button--saving" : ""}`} disabled={saveNotes.isPending}>
              {saveNotes.isPending ? "Salvataggio..." : "Salva note"}
            </button>
            {notesState && <p className={`field-notes__state field-notes__state--${notesState.tone}`} role={notesState.tone === "error" ? "alert" : "status"}>{notesState.text}</p>}
          </form>
        ) : (
          <p className="field-notes__read">{item.fieldNotes || "Nessuna nota dal campo."}</p>
        )}
      </section>

      <section className="detail-card photo-section"><div className="section-heading"><div><p className="eyebrow">Documentazione</p><h2>Fotografie</h2></div><span className="metric"><Camera size={18} /> {photos.data?.length ?? 0}</span></div><div className="photo-actions">{(["before", "during", "after"] as const).map((category) => <label className="upload-button" key={category}><Upload size={19} /><span>{category === "before" ? "Prima" : category === "during" ? "Durante" : "Dopo"}</span><input type="file" accept="image/jpeg,image/png,image/heic" disabled={uploading} onChange={(event) => void uploadPhoto(event, category)} /></label>)}</div><div className="photo-grid">{photos.data?.map((photo) => <figure key={photo.id}><img src={`/api/intervention-files/${photo.id}`} alt={`Fotografia ${photo.category}`} /><figcaption>{photo.category === "before" ? "Prima" : photo.category === "during" ? "Durante" : "Dopo"}</figcaption></figure>)}</div></section>

      {registroPiscine && isOpen && piscineDelPosto.length > 0 && (
        <section className="detail-card">
          <PoolReadingForm pools={piscineDelPosto} interventionId={id} onSaved={() => void utils.pools.invalidate()} />
        </section>
      )}

      {isOpen && (
        <div className="completion-bar">
          <span><strong>Lavoro concluso?</strong><small>{ownActiveTimer ? "Chiudendo, il tuo timer si ferma da solo." : "Spunta le attività fatte, poi chiudi."}</small></span>
          <button className="button button--primary" type="button" onClick={() => void finish()} disabled={complete.isPending || stop.isPending}>
            {complete.isPending || stop.isPending ? "Chiusura..." : "Lavoro concluso"}
          </button>
          {closing && <p className={`completion-bar__state completion-bar__state--${closing.tone}`} role={closing.tone === "error" ? "alert" : "status"}>{closing.text}</p>}
        </div>
      )}
      {!isOpen && closing && <p className="notice" role="status">{closing.text}</p>}
    </main>
  );
}
